import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Button,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Spinner,
} from "react-bootstrap";
import debounce from "lodash/debounce";
import { searchChats } from "@/entities/search/api";
import { Chat, NewContact } from "@/entities/chat";
import PhoneInput from "react-phone-input-2";
import { useSnackbar } from "notistack";
import { useInfiniteSearch } from "@/shared/hooks";
import { formatPhone } from "@/shared/ui";

interface NewChatModalProps {
  show: boolean;
  onHide: () => void;
  addNewChat: (newContact: NewContact) => void;
  onChatSelected?: (chat: Chat) => void;
}

export const NewChatModal: React.FC<NewChatModalProps> = ({
  show,
  onHide,
  addNewChat,
  onChatSelected,
}) => {
  const [searchInputError, setSearchInputError] = useState<string | null>(null);

  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContact, setNewContact] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });
  const [addContactError, setAddContactError] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  const searchFn = useCallback(
    ({ query, page }: { query: string; page: number }) =>
      searchChats({ query, page }),
    []
  );

  const {
    query: searchInput,
    setQuery: setSearchInput,
    results: searchResults,
    loading: loadingSearch,
    hasMore,
    totalCount,
    containerRef,
    reset,
  } = useInfiniteSearch<Chat>({ searchFn, debounceDelay: 500 });

  const { enqueueSnackbar } = useSnackbar();

  const validatePhone = (value: string) => {
    if (/[a-zA-Zа-яА-Я]/.test(value)) return "Введите номер";
    const phoneRegex = /^\+?\d{10,15}$/;
    if (!phoneRegex.test(value)) return "Некорректный номер";
    return null;
  };

  useEffect(() => {
    if (showAddContactModal && searchInput) {
      const digitsOnly = searchInput.replace(/\D/g, "");
      if (digitsOnly.length >= 10) {
        let formattedPhone = digitsOnly;
        if (formattedPhone.startsWith("8")) {
          formattedPhone = "7" + formattedPhone.slice(1);
        } else if (!formattedPhone.startsWith("7")) {
          formattedPhone = "7" + formattedPhone;
        }
        if (formattedPhone.length <= 11) {
          setNewContact((prev) => ({ ...prev, phone: formattedPhone }));
        }
      }
    }
  }, [showAddContactModal, searchInput]);

  const handleInvite = async () => {
    setAddContactError(null);

    // if (!newContact.firstName.trim()) {
    //   setAddContactError("Введите имя");
    //   return;
    // }

    // if (!newContact.lastName.trim()) {
    //   setAddContactError("Введите фамилию");
    //   enqueueSnackbar("Введите фамилию", { variant: "error" });
    //   return;
    // }

    const phoneError = validatePhone(newContact.phone);
    if (phoneError) {
      setAddContactError(phoneError);
      return;
    }

    setInviteLoading(true);
    try {
      const digits = newContact.phone.replace(/\D/g, "").replace(/^7/, "8");
      addNewChat({ ...newContact, phone: digits });
      setShowAddContactModal(false);
      onHide();
    } catch (err) {
      setAddContactError("Ошибка отправки приглашения");
    } finally {
      setInviteLoading(false);
    }
  };

  // Сброс состояний при закрытии
  useEffect(() => {
    if (!show) {
      reset();
      setShowAddContactModal(false);
      setNewContact({ firstName: "", lastName: "", phone: "" });
      setAddContactError(null);
    }
  }, [show]);

  return (
    <>
      <Modal
        className={showAddContactModal ? "add-contact-active" : ""}
        show={show && !showAddContactModal}
        onHide={onHide}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ whiteSpace: "nowrap" }}>Новый чат</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup>
            <Form.Control
              placeholder="Введите имя или номер"
              className="rounded-5"
              value={searchInput}
              isInvalid={!!searchInputError}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setSearchInputError(null);
              }}
            />
            {loadingSearch && (
              <InputGroup.Text>
                <Spinner size="sm" />
              </InputGroup.Text>
            )}
            {searchInputError && (
              <div className="invalid-feedback d-block">{searchInputError}</div>
            )}
          </InputGroup>

          {searchResults.length > 0 ? (
            <div
              ref={containerRef}
              className="scrollbar-custom-y"
              style={{ maxHeight: 400, overflowY: "auto" }}
            >
              <ListGroup className="mt-2">
                {searchResults.map((chat) => (
                  <ListGroup.Item
                    key={chat._id}
                    action
                    onClick={() => {
                      onChatSelected?.(chat);
                      onHide();
                    }}
                  >
                    <div className="d-flex flex-column">
                      <span>{chat?.contact?.firstName}</span>
                      <small className="text-muted">
                        {formatPhone(chat?.contact?.user?.phone)}
                      </small>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
              {/* ) : (
              <>
               <div className="text-center mt-2">Нет результатов</div>
              
          {  searchInput && (
              <div className="mt-3">
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowAddContactModal(true)}
                >
                  Добавить контакт
                </Button>
              </div>
           ) }
              </>
             )} */}
              {loadingSearch && (
                <div className="text-center mt-2">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
            </div>
          ) : (
            searchInput && (
              <div className="mt-3">
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowAddContactModal(true)}
                >
                  Добавить контакт
                </Button>
              </div>
            )
          )}

          {/* {inviteLink && (
            <div className="mt-3">
              <strong>Ссылка на приглашение:</strong>{" "}
              <a href={inviteLink}>{inviteLink}</a>
            </div>
          )} */}
        </Modal.Body>
      </Modal>

      <Modal
        show={showAddContactModal}
        onHide={() => setShowAddContactModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Добавить контакт</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Имя</Form.Label>
            <Form.Control
              className="rounded-5"
              value={newContact.firstName}
              placeholder="Введите имя"
              onChange={(e) =>
                setNewContact({ ...newContact, firstName: e.target.value })
              }
              isInvalid={!!addContactError && !newContact.firstName.trim()}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Фамилия</Form.Label>
            <Form.Control
              className="rounded-5"
              placeholder="Введите фамилию"
              value={newContact.lastName}
              onChange={(e) =>
                setNewContact({ ...newContact, lastName: e.target.value })
              }
              isInvalid={!!addContactError && !newContact.lastName.trim()}
            />
          </Form.Group>

          <Form.Group controlId="formPhone" className="mb-3">
            <Form.Label>Номер</Form.Label>
            <PhoneInput
              country={"ru"}
              disableDropdown={true}
              specialLabel=""
              // countryCodeEditable={false}
              masks={{ ru: "(...) ...-..-.." }}
              onlyCountries={["ru"]}
              placeholder="Введите номер телефона"
              value={newContact.phone}
              onChange={(phone) => {
                if (phone.startsWith("8")) {
                  setNewContact({ ...newContact, phone: `7${phone.slice(1)}` })
                } else {
                  setNewContact({ ...newContact, phone })
                }
              }}
              inputClass="form-control rounded-5"
            />
            <Form.Control.Feedback type="invalid">
              Введите корректный номер телефона.
            </Form.Control.Feedback>
          </Form.Group>

          <Button
            variant="outline-secondary"
            disabled={inviteLoading}
            onClick={handleInvite}
          >
            Пригласить
            {inviteLoading && <Spinner size="sm" className="ms-1" />}
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
};
